/**
 * CollabBar — compact avatar row shown in ProjectWorkspace top bar.
 * Shows up to 4 avatars + overflow count.
 * Hover shows: email, current file, role, status.
 */
import React, { useState } from 'react';

function Avatar({ member, size = 28 }) {
  const [hover, setHover] = useState(false);
  const initials = (member.email || '?').slice(0, 2).toUpperCase();
  const isTyping = member.status === 'typing';

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: member.color || '#6366f1',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.36, fontWeight: 700, color: '#fff',
        border: `2px solid ${member.status === 'online' || isTyping ? (member.color || '#6366f1') : '#475569'}`,
        cursor: 'default', flexShrink: 0, position: 'relative',
        boxShadow: member.status === 'online' ? `0 0 0 2px rgba(${hexToRgb(member.color)},0.3)` : 'none',
        transition: 'box-shadow 0.2s',
      }}>
        {initials}
        {/* Online dot */}
        <span style={{
          position: 'absolute', bottom: 0, right: 0,
          width: 8, height: 8, borderRadius: '50%',
          background: isTyping ? '#f59e0b' : member.status === 'online' ? '#10b981' : '#475569',
          border: '1.5px solid var(--bg-secondary)',
        }} />
      </div>

      {hover && (
        <div style={{
          position: 'absolute', top: size + 6, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '8px 12px', zIndex: 9999,
          minWidth: 160, boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{member.email}</p>
          <p style={{ margin: '3px 0 0', fontSize: 11, color: member.color || '#6366f1', fontWeight: 600, textTransform: 'capitalize' }}>
            {member.role}
          </p>
          {member.current_file && (
            <p style={{ margin: '3px 0 0', fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
              📄 {member.current_file}
            </p>
          )}
          <p style={{ margin: '3px 0 0', fontSize: 10, color: isTyping ? '#f59e0b' : member.status === 'online' ? '#10b981' : '#64748b' }}>
            {isTyping ? '✏️ typing...' : member.status === 'online' ? '● online' : '○ offline'}
          </p>
        </div>
      )}
    </div>
  );
}

function hexToRgb(hex) {
  if (!hex) return '99,102,241';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

export default function CollabBar({ members = [], myColor, connected, isViewer = false, onOpenInvite, onOpenActivity }) {
  const visible = members.slice(0, 4);
  const overflow = members.length - 4;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {/* Connection indicator */}
      <span style={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
        background: connected ? '#10b981' : '#ef4444',
        boxShadow: connected ? '0 0 6px #10b981' : 'none',
      }} title={connected ? 'Collaboration active' : 'Disconnected'} />

      {/* Avatars */}
      <div style={{ display: 'flex', gap: -4 }}>
        {visible.map(m => <Avatar key={m.user_id} member={m} size={26} />)}
        {overflow > 0 && (
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
          }}>
            +{overflow}
          </div>
        )}
      </div>

      {/* Invite button — hidden for viewers */}
      {!isViewer && (
        <button
          onClick={onOpenInvite}
          style={{
            padding: '3px 10px', fontSize: 11, fontWeight: 600,
            background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)',
            borderRadius: 6, color: '#818cf8', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
          title="Invite collaborators"
        >
          + Invite
        </button>
      )}

      {/* Activity button */}
      <button
        onClick={onOpenActivity}
        style={{
          padding: '3px 8px', fontSize: 11, fontWeight: 600,
          background: 'transparent', border: '1px solid var(--border)',
          borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer',
        }}
        title="Activity feed"
      >
        📋
      </button>
    </div>
  );
}
